import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ClientLayout from '../../components/ClientLayout';
import PageHeader from '../../components/client/PageHeader';
import ExpertCard from '../../components/client/ExpertCard';
import { clientPortalApi } from '../../lib/http';

const MyExperts: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: experts,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['client-added-experts'],
    queryFn: async () => {
      const response = await clientPortalApi.experts.listAdded();
      return response.data;
    },
    staleTime: 1 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const handleFavoriteToggle = (expertSlug: string, isFavorite: boolean) => {
    refetch();
  };

  const handleExpertClick = (slug: string) => {
    navigate(`/u/experts/${slug}`);
  };

  // Filter experts based on search query
  const filteredExperts = experts?.filter((expert) =>
    expert.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  return (
    <ClientLayout>
      <div className="w-full">
        {/* Page Header */}
        <PageHeader title="My Experts" backTo="/u" />

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search experts..."
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Count */}
        {!isLoading && experts && experts.length > 0 && (
          <p className="text-xs sm:text-sm text-gray-500 mb-3">
            {searchQuery
              ? `${filteredExperts.length} of ${experts.length} experts`
              : `${experts.length} expert${experts.length === 1 ? '' : 's'}`}
          </p>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-[72px] bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && (!experts || experts.length === 0) && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-50 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-purple-400"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No experts yet</h3>
            <p className="text-sm text-gray-500 mb-4">
              Book with experts to see them here
            </p>
            <button
              onClick={() => navigate('/u')}
              className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-xl hover:bg-purple-700 transition-colors active:scale-95"
            >
              Find Experts
            </button>
          </div>
        )}

        {/* No Search Results */}
        {!isLoading && experts && experts.length > 0 && filteredExperts.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No results found</h3>
            <p className="text-sm text-gray-500">
              No experts match "{searchQuery}"
            </p>
          </div>
        )}

        {/* Experts List */}
        {!isLoading && filteredExperts.length > 0 && (
          <div className="space-y-3">
            {filteredExperts.map((expert) => (
              <ExpertCard
                key={expert.slug}
                slug={expert.slug}
                displayName={expert.displayName}
                isFavorite={expert.isFavorite !== undefined ? expert.isFavorite : false}
                subtitle="Your expert"
                onClick={handleExpertClick}
                onFavoriteToggle={(isFav) => handleFavoriteToggle(expert.slug, isFav)}
              />
            ))}
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default MyExperts;
